import { FC, useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DataList, MessageGuide, MessageModal } from 'common/components';
import {
    ACTION_ITEMS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE,
    MAIN_ROUTES
} from 'common/config';
import { Action, ButtonLabel, EntityActions, MessageType } from 'common/enums';
import { ConfigurationTypes } from 'modules/configuration/enums/configuration.enum';
import { RootState } from 'common/redux/core/root.reducer';
import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import { VIDEO_LINK_TITLE, getVideoLinkColDefs } from 'modules/configuration/config';
import { configurationActions } from 'modules/configuration/redux';
import {
    DeleteVideoLinkPayload,
    VideoDetails,
    VideoLinkDTO
} from 'modules/configuration/models/configuration.model';
import { getVideoLinkResponse } from 'modules/configuration/redux/configuration.selector';
import { VIDEO_LINK_GUIDE } from 'common/config/message.config';

import './VideoLink.scss';
import { getSerialNumberedData } from 'common/utils/data';
import useVideoLinkAccess from 'modules/configuration/roles-access-hooks/useVideoLinkAccess';

const VideoLink: FC = () => {
    const [searchVal, setSearchVal] = useState<string>('');
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [modalData, setModalData] = useState<string>('');
    const { isSetUpDisabled } = useVideoLinkAccess();
    const videoLinkResponse = useSelector((state: RootState) => getVideoLinkResponse(state));

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(configurationActions.fetchVideoLink());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */
    const onSearchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    const getData = () => {
        if (searchVal.length > 0) {
            const videoData = videoLinkResponse.data?.filter((item: VideoDetails) =>
                item.title.toLowerCase().includes(searchVal.toLowerCase())
            );
            return getSerialNumberedData(videoData, 'order');
        }
        return (
            (videoLinkResponse.data && getSerialNumberedData(videoLinkResponse.data, 'order')) || []
        );
    };

    const handleDelete = (data: VideoLinkDTO) => {
        setPopupDetails({
            title: DELETE_POPUP_TITLE,
            text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
            type: EntityActions.Delete
        });
        setModalData(data?.videoDetailId);
        setShowPopup(true);
    };

    const popupHandler = (id: string) => {
        if (popupDetails.type == EntityActions.Delete) {
            const apiPayload: DeleteVideoLinkPayload = {
                videoDetailId: id
            };
            dispatch(configurationActions.deleteVideoLink(apiPayload));
        }
        setShowPopup(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleAction = (action: string | number, data: any) => {
        switch (action) {
            case Action.Edit: {
                navigate(MAIN_ROUTES.VIDEO_LINKS_CREATE_NEW, {
                    state: {
                        data: data,
                        action
                    }
                });
                break;
            }
            case Action.Delete: {
                handleDelete(data);
                break;
            }
        }
    };

    const addNewHandler = () => {
        navigate(MAIN_ROUTES.VIDEO_LINKS_CREATE_NEW, {
            state: {
                action: Action.Create
            }
        });
    };

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={VIDEO_LINK_TITLE}
                buttonTitle={ACTION_ITEMS.ADD_NEW_BUTTON}
                searchVal={searchVal}
                isButtonVisible={true}
                isAddNewDisabled={isSetUpDisabled}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={addNewHandler}
            />
            <MessageGuide message={VIDEO_LINK_GUIDE} />
            <DataList
                isNoDataVisible={getData()?.length === 0}
                data={getData()}
                columnDefs={getVideoLinkColDefs(handleAction, isSetUpDisabled)}
            />
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible
                type={MessageType.Warning}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => popupHandler(modalData)}
                onSecondaryAction={() => {
                    setShowPopup(false);
                }}
            />
        </div>
    );
};

export default VideoLink;
